import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';

// Components
import FacultyStatsCard from '../Components/Dashboard/FacultyStatsCard';
import ProgramComparisonTable from '../Components/Dashboard/ProgramComparisonTable';
import MultiBellCurveChart from '../Components/Dashboard/MultiBellCurveChart';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
  }
}));

const FacultyDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facultyData, setFacultyData] = useState(null);
  const [programsData, setProgramsData] = useState([]);
  const [academicYear, setAcademicYear] = useState('2568');

  useEffect(() => {
    fetchFacultyData();
  }, [academicYear]);

  const fetchFacultyData = async () => {
    setLoading(true);
    try {
      // จำลองข้อมูล - ในอนาคตจะเรียก API จริง
      const mockFacultyData = {
        faculty_overall: {
          total_students: 485,
          avg_total_score: 72.5,
          avg_evaluation_15: 62.3,
          avg_satisfaction: 8.2,
          std_deviation: 8.9,
          academic_year: "2568",
          total_companies: 156,
          completion_rate: 94.8
        }
      };

      const mockProgramsData = [
        {
          program_name: "วศ.บ.วิศวกรรมคอมพิวเตอร์",
          program_code: "CPE",
          student_count: 120,
          avg_score: 74.2,
          std_deviation: 8.5,
          min_score: 55,
          max_score: 84,
          top_10_percent: 15,
          color: '#2196f3',
          skill_breakdown: {
            performance: 4.2,
            skills: 4.1,
            communication: 3.9,
            leadership: 3.8,
            behavior: 4.3,
            attitude: 4.0
          }
        },
        {
          program_name: "วศ.บ.วิศวกรรมไฟฟ้า",
          program_code: "EE",
          student_count: 95,
          avg_score: 71.8,
          std_deviation: 9.2,
          min_score: 52,
          max_score: 82,
          top_10_percent: 12,
          color: '#f44336',
          skill_breakdown: {
            performance: 4.0,
            skills: 3.9,
            communication: 3.7,
            leadership: 3.6,
            behavior: 4.1,
            attitude: 3.8
          }
        },
        {
          program_name: "วศ.บ.วิศวกรรมเครื่องกล",
          program_code: "ME",
          student_count: 110,
          avg_score: 70.5,
          std_deviation: 7.8,
          min_score: 54,
          max_score: 81,
          top_10_percent: 10,
          color: '#4caf50',
          skill_breakdown: {
            performance: 3.9,
            skills: 3.8,
            communication: 3.6,
            leadership: 3.5,
            behavior: 4.0,
            attitude: 3.7
          }
        },
        {
          program_name: "วศ.บ.วิศวกรรมโยธา",
          program_code: "CE",
          student_count: 85,
          avg_score: 72.1,
          std_deviation: 8.9,
          min_score: 51,
          max_score: 83,
          top_10_percent: 13,
          color: '#ff9800',
          skill_breakdown: {
            performance: 4.1,
            skills: 4.0,
            communication: 3.8,
            leadership: 3.7,
            behavior: 4.2,
            attitude: 3.9
          }
        },
        {
          program_name: "วศ.บ.วิศวกรรมอุตสาหการ",
          program_code: "IE",
          student_count: 75,
          avg_score: 69.8,
          std_deviation: 9.5,
          min_score: 48,
          max_score: 80,
          top_10_percent: 8,
          color: '#9c27b0',
          skill_breakdown: {
            performance: 3.8,
            skills: 3.7,
            communication: 3.5,
            leadership: 3.4,
            behavior: 3.9,
            attitude: 3.6
          }
        }
      ];

      // จำลองการ delay API
      await new Promise(resolve => setTimeout(resolve, 1500));

      setFacultyData(mockFacultyData);
      setProgramsData(mockProgramsData);
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchFacultyData();
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>กำลังโหลดข้อมูลคณะ...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mx: 'auto', maxWidth: 600 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <style jsx global>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 12pt !important; }
        }
      `}</style>
      
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box 
          mb={4} 
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            borderRadius: 3,
            p: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.4)',
          }}
        >
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="flex-start"
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  mb: 1,
                }}
              >
                📈 ภาพรวมการประเมินการฝึกงาน
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'medium',
                  fontSize: '1.2rem',
                  mb: 2,
                  opacity: 0.9,
                }}
              >
                คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip 
                  label={`ปีการศึกษา ${academicYear}`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                />
                <Chip 
                  icon={<GroupIcon />}
                  label={`${facultyData?.faculty_overall?.total_students || 0} นิสิต`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                />
                <Chip 
                  icon={<BusinessIcon />}
                  label={`${facultyData?.faculty_overall?.total_companies || 0} บริษัท`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
              <Tooltip title="รีเฟรชข้อมูล" arrow>
                <IconButton 
                  onClick={handleRefresh}
                  size="large"
                  sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': { 
                      background: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="พิมพ์รายงาน" arrow>
                <IconButton 
                  onClick={handlePrint}
                  size="large"
                  sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': { 
                      background: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Faculty Statistics Overview */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12}>
            <FacultyStatsCard 
              data={facultyData?.faculty_overall}
              academicYear={academicYear}
            />
          </Grid>
        </Grid>

        {/* Program Comparison */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <SchoolIcon color="primary" />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    🎓 เปรียบเทียบผลตามหลักสูตร
                  </Typography>
                </Box>
                <ProgramComparisonTable programs={programsData} />
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Bell Curve Charts */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <TrendingUpIcon color="primary" />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    📊 การกระจายคะแนนแต่ละหลักสูตร
                  </Typography>
                </Box>
                <MultiBellCurveChart 
                  programs={programsData}
                  facultyAverage={facultyData?.faculty_overall?.avg_total_score}
                />
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box 
          mt={4} 
          py={3} 
          textAlign="center" 
          sx={{ 
            borderTop: '1px solid #e0e0e0',
            color: 'text.secondary'
          }}
          className="no-print"
        >
          <Typography variant="body2">
            ระบบจัดการการฝึกงาน คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา | 
            ข้อมูล ณ วันที่ {new Date().toLocaleDateString('th-TH')}
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default FacultyDashboard;
